import React, { useState, useEffect } from 'react';
import { Truck, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Admin Finance: shipping revenue vs carrier cost (validates the shipping margin model)
export const ShippingMarginReport = ({ adminUser }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminUser?.id) return;
    fetch(`${BACKEND_URL}/api/admin/shipping/margins/${adminUser.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setReport(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [adminUser?.id]);

  if (loading) return null;
  if (!report) return null;

  const tiles = [
    { label: 'Shipments', value: report.total_shipments },
    { label: 'Charged to buyers', value: `$${report.total_retail_charged.toFixed(2)}` },
    { label: 'Paid to carriers', value: `$${report.total_bulk_paid.toFixed(2)}` },
    { label: 'Shipping margin', value: `$${report.total_margin_profit.toFixed(2)}`, highlight: true },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm p-6" data-testid="shipping-margin-report">
      <h3 className="font-semibold text-gray-900 text-lg mb-1 flex items-center gap-2">
        <Truck className="w-5 h-5 text-minted-600" /> Shipping Margin
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        What buyers paid for shipping vs what carriers charged us — validate the margin before funding seller acquisition.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {tiles.map(t => (
          <div key={t.label} className={`rounded-lg p-3 text-center ${t.highlight ? 'bg-minted-50 border border-minted-200' : 'bg-gray-50'}`}>
            <p className={`text-lg font-bold ${t.highlight ? 'text-minted-700' : 'text-gray-900'}`}>{t.value}</p>
            <p className="text-[10px] uppercase tracking-wide text-gray-500">{t.label}</p>
          </div>
        ))}
      </div>
      {report.total_shipments > 0 && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <TrendingUp className="w-3.5 h-3.5 text-minted-600" />
          Average margin ${report.avg_margin_per_shipment.toFixed(2)} per shipment
          {Object.keys(report.by_carrier || {}).length > 0 && (
            <span> · {Object.entries(report.by_carrier).map(([c, s]) => `${c}: ${s.count} (${s.total_margin >= 0 ? '+' : ''}$${s.total_margin.toFixed(2)})`).join(' · ')}</span>
          )}
        </p>
      )}
      {report.total_shipments === 0 && (
        <p className="text-xs text-gray-400">No label purchases recorded yet — data appears as soon as orders ship with Minted labels.</p>
      )}
    </div>
  );
};

export default ShippingMarginReport;
